import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { useAppSelector } from 'hooks/reduxHooks'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'

const BannerWrap = styled.div`
  margin: 0 ${rem(-24)};

  &.flush {
    margin: 0;
  }
`

/**
 * Promotional banner shown inside the Maldives charter calendar when reward fare
 * classes are available. Encourages members to top up their loyalty points.
 */
interface Props {
  flush?: boolean
}

function MaldivesCharterBuyPointsBanner({ flush = false }: Props) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points)
  const isLoggedIn = !!memberId

  const richTextValues = {
    loyaltyProgramName,
    bold: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
    link: (chunks: React.ReactNode) => (
      <TextLink size="medium" weight="regular" to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`} target="_blank">{chunks}</TextLink>
    ),
  }

  const message = isLoggedIn && userPoints != null
    ? intl.formatMessage(
        {
          id: 'lsBWKkElo7uqaLHyciTj',
          defaultMessage: '<bold>You have {points}.</bold> Need more {loyaltyProgramName} Points? Limited-time bonus on top-ups. <link>Top-up points</link>',
          description: 'Promotional banner in the Maldives charter calendar showing the logged-in user their current points balance and encouraging them to top up loyalty points.',
        },
        { ...richTextValues, points: <FormatLoyaltyPoints points={userPoints} /> },
      )
    : intl.formatMessage(
        {
          id: 'i9PHVOKLu0AnjfiFPGvy',
          defaultMessage: '<bold>Need more {loyaltyProgramName} Points?</bold> Limited-time bonus on top-ups. <link>Top-up points</link>',
          description: 'Promotional banner in the Maldives charter calendar encouraging logged-out users to top up loyalty points with a limited-time bonus offer.',
        },
        richTextValues,
      )

  return (
    <BannerWrap className={flush ? 'flush' : undefined}>
      <MessageBanner
        kind="promotional"
        icon={<LineInfoCircleIcon />}
        density="compact"
      >
        {message}
      </MessageBanner>
    </BannerWrap>
  )
}

export default MaldivesCharterBuyPointsBanner
